import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CohortDetailPage() {
  const { cohortId } = useParams();
  const navigate = useNavigate();
  const [cohortData, setCohortData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const { getToken } = useAdminAuth();

  useEffect(() => {
    fetchCohortDetail();
  }, [cohortId]);

  const fetchCohortDetail = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/cohorts/${cohortId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setCohortData(response.data);
    } catch (error) {
      console.error('Error fetching cohort detail:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div>
          <p className="mt-4 text-gray-400">Cargando detalles del cohorte...</p>
        </div>
      </div>
    );
  }

  if (!cohortData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Cohorte no encontrado</p>
      </div>
    );
  }

  const { cohort, users, sessions, stats } = cohortData;

  const getStatusBadge = (status) => {
    const styles = {
      upcoming: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      active: 'bg-green-500/10 text-green-400 border-green-500/30',
      preview: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      completed: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
      suspended: 'bg-red-500/10 text-red-400 border-red-500/30',
    };
    return styles[status] || styles.upcoming;
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <button onClick={() => navigate('/admin/dashboard')} className="hover:text-[#D4AF37]">
          Admin
        </button>
        <span>/</span>
        <button onClick={() => navigate('/admin/cohorts')} className="hover:text-[#D4AF37]">
          Cohortes
        </button>
        <span>/</span>
        <span className="text-white">{cohort.name}</span>
      </div>

      {/* Header */}
      <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">{cohort.name}</h1>
            <p className="text-gray-400">{cohort.description || 'Sin descripción'}</p>
          </div>
          <span
            className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusBadge(
              cohort.status
            )}`}
          >
            {cohort.status}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-black/30 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Total Estudiantes</div>
            <div className="text-2xl font-bold text-white">{stats.total_users}</div>
          </div>
          <div className="bg-black/30 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Activos</div>
            <div className="text-2xl font-bold text-green-400">{stats.active_users}</div>
          </div>
          <div className="bg-black/30 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Preview</div>
            <div className="text-2xl font-bold text-yellow-400">{stats.preview_users}</div>
          </div>
          <div className="bg-black/30 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Sesiones</div>
            <div className="text-2xl font-bold text-[#D4AF37]">{stats.total_sessions}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Fecha de Inicio:</span>
            <span className="text-white">
              {new Date(cohort.start_date).toLocaleDateString('es-ES')}
            </span>
          </div>
          {cohort.end_date && (
            <div className="flex justify-between">
              <span className="text-gray-400">Fecha de Fin:</span>
              <span className="text-white">
                {new Date(cohort.end_date).toLocaleDateString('es-ES')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1a4d5c]/30">
        <div className="flex border-b border-[#1a4d5c]/30">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'users'
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Estudiantes ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'sessions'
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Sesiones ({sessions.length})
          </button>
        </div>

        <div className="p-6">
          {/* Users Tab */}
          {activeTab === 'users' && (
            <div>
              {users.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-black/50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Usuario
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Fecha Registro
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-700/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E4B7] flex items-center justify-center text-black font-bold">
                                {user.full_name?.charAt(0).toUpperCase()}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-white">{user.full_name}</div>
                                <div className="text-xs text-gray-400">ID: {user.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-300">{user.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(
                                user.enrollment?.status || 'preview'
                              )}`}
                            >
                              {user.enrollment?.status || 'preview'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                            {new Date(user.created_at).toLocaleDateString('es-ES')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => navigate(`/admin/users/${user.id}`)}
                              className="text-[#D4AF37] hover:text-[#F4E4B7] font-medium"
                            >
                              Ver detalle
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400">No hay estudiantes asignados a este cohorte</p>
                </div>
              )}
            </div>
          )}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <div>
              {sessions.length > 0 ? (
                <div className="space-y-4">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30 hover:border-[#D4AF37]/50 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-lg font-bold text-white mb-1">{session.title}</h4>
                          <p className="text-sm text-gray-400">{session.pilar}</p>
                        </div>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(
                            session.status
                          )}`}
                        >
                          {session.status}
                        </span>
                      </div>
                      <div className="mt-4 flex items-center gap-6 text-sm text-gray-400">
                        <div>
                          📅 {new Date(session.date).toLocaleDateString('es-ES', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                        <div>⏱️ {session.duration} minutos</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400">No hay sesiones programadas para este cohorte</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
